// ============================================================================
// LIGA AJUPAM — API: Encuestas de satisfacción (v6.7.0)
// ============================================================================
// Wrapper de los RPCs definidos en migrations 20260426_v6_7_0_02..05.
// Sigue el patrón de bonus.ts: cada función es un thin wrapper sobre supabase.rpc.
// ============================================================================

import { supabase } from './client';

export type SurveyPhase = 'REGULAR' | 'PLAYOFFS';
export type SurveyQuestionType = 'RATING_1_5' | 'TEXT' | 'RADIO' | 'CHECKBOX';
export type SurveyPhaseManualState = 'AUTO' | 'OPEN' | 'CLOSED';

export interface SurveyQuestion {
  id: string;
  phase: SurveyPhase;
  category: string;
  question_text: string;
  question_type: SurveyQuestionType;
  options: string[] | null;
  // v6.11.128.02 — opciones que, al marcarse, exigen texto libre (p. ej. ["Otro"]).
  // En la 4ta «Otro» fue la 2ª opción más votada y quedó ilegible por no pedirlo.
  options_requiring_text?: string[] | null;
  // v6.11.220 — pregunta atada a una función de la liga (hoy sólo Desafíos): se
  // muestra y se exige únicamente si esa función está activada. `feature_active` lo
  // calcula el servidor y sólo lo devuelve admin_list_survey_questions.
  requires_feature?: 'challenges' | null;
  feature_active?: boolean;
  is_required: boolean;
  order_index: number;
  is_active: boolean;
  is_default: boolean;
  response_count?: number;
  created_at?: string;
  updated_at?: string;
}

export interface SurveyAnswerInput {
  question_id: string;
  rating?: number;
  text?: string;
  options?: string | string[];
}

export interface ActiveSurveyPhase {
  phase: SurveyPhase;
  is_open: boolean;
  user_can_access: boolean;
  already_submitted: boolean;
  /** v6.11.329 — La app bloquea todas las secciones hasta responderla. Sin la migración, no viene. */
  must_answer?: boolean;
  questions: SurveyQuestion[];
}

export interface ActiveSurveysResponse {
  has_team?: boolean;
  team_id?: string | null;
  category_id?: string | null;
  branch?: string | null;
  league_id?: string | null;
  qualifies_playoffs?: boolean;
  phases: ActiveSurveyPhase[];
}

// v6.11.128.01 — invitación blanda a la encuesta de playoffs.
export interface PlayoffsSurveyInvite {
  should_invite: boolean;
  league_id?: string;
  league_name?: string;
  team_id?: string;
}

// ============================================================================
// USER-SIDE
// ============================================================================

export const getActiveSurveys = async (sessionToken: string): Promise<ActiveSurveysResponse> => {
  const { data, error } = await (supabase.rpc as any)('user_get_active_surveys', {
    p_session_token: sessionToken,
  });
  if (error) throw error;
  return data || { phases: [] };
};

export const submitSurvey = async (
  sessionToken: string,
  phase: SurveyPhase,
  answers: SurveyAnswerInput[],
  isAnonymous: boolean
): Promise<{ success: boolean; response_id: string }> => {
  const { data, error } = await (supabase.rpc as any)('user_submit_survey', {
    p_session_token: sessionToken,
    p_phase: phase,
    p_answers: answers,
    p_is_anonymous: isAnonymous,
  });
  if (error) throw error;
  return data;
};

/**
 * v6.11.128.01 — ¿Le ofrecemos al jugador la encuesta de playoffs de su edición
 * anterior? Se consulta en la inscripción a la edición siguiente. Es un
 * ofrecimiento: el jugador puede omitirla y seguir.
 */
export const getPlayoffsSurveyInvite = async (
  sessionToken: string
): Promise<PlayoffsSurveyInvite> => {
  const { data, error } = await (supabase.rpc as any)('user_get_playoffs_survey_invite', {
    p_session_token: sessionToken,
  });
  if (error) throw error;
  return data || { should_invite: false };
};

// ============================================================================
// ADMIN-SIDE
// ============================================================================

/**
 * v6.11.128.01 — Abre la encuesta de PLAYOFFS y notifica a los clasificados que
 * todavía no respondieron. En la 4ta nadie se enteró de que existía.
 */
export const adminOpenPlayoffsSurvey = async (
  adminSessionToken: string,
  leagueId?: string | null
): Promise<{ success: boolean; league_id: string; notified: number }> => {
  const { data, error } = await (supabase.rpc as any)('admin_open_playoffs_survey', {
    p_admin_session_token: adminSessionToken,
    p_league_id: leagueId ?? null,
  });
  if (error) throw error;
  return data;
};

export const adminListSurveyQuestions = async (
  adminSessionToken: string,
  phase: SurveyPhase | null = null,
  leagueId?: string | null
): Promise<SurveyQuestion[]> => {
  const { data, error } = await (supabase.rpc as any)('admin_list_survey_questions', {
    p_admin_session_token: adminSessionToken,
    p_phase: phase,
    p_league_id: leagueId ?? null,
  });
  if (error) throw error;
  return data || [];
};

export interface CreateQuestionInput {
  phase: SurveyPhase;
  category: string;
  question_text: string;
  question_type: SurveyQuestionType;
  options?: string[] | null;
  is_required?: boolean;
  order_index?: number | null;
}

// v6.7.17: leagueId opcional. Sin él, el RPC default-ea a la liga current —
// y order_index también se calcula por (phase, league_id) para que cada liga
// tenga su propia secuencia de preguntas.
export const adminCreateSurveyQuestion = async (
  adminSessionToken: string,
  input: CreateQuestionInput,
  leagueId?: string | null
): Promise<{ success: boolean; id: string }> => {
  const { data, error } = await (supabase.rpc as any)('admin_create_survey_question', {
    p_admin_session_token: adminSessionToken,
    p_phase: input.phase,
    p_category: input.category,
    p_question_text: input.question_text,
    p_question_type: input.question_type,
    p_options: input.options ?? null,
    p_is_required: input.is_required ?? false,
    p_order_index: input.order_index ?? null,
    p_league_id: leagueId ?? null,
  });
  if (error) throw error;
  return data;
};

export interface UpdateQuestionInput {
  category?: string | null;
  question_text?: string | null;
  options?: string[] | null;
  is_required?: boolean | null;
  order_index?: number | null;
}

export const adminUpdateSurveyQuestion = async (
  adminSessionToken: string,
  questionId: string,
  updates: UpdateQuestionInput
): Promise<{ success: boolean; id: string }> => {
  const { data, error } = await (supabase.rpc as any)('admin_update_survey_question', {
    p_admin_session_token: adminSessionToken,
    p_question_id: questionId,
    p_category: updates.category ?? null,
    p_question_text: updates.question_text ?? null,
    p_options: updates.options ?? null,
    p_is_required: updates.is_required ?? null,
    p_order_index: updates.order_index ?? null,
  });
  if (error) throw error;
  return data;
};

export const adminToggleSurveyQuestion = async (
  adminSessionToken: string,
  questionId: string,
  isActive: boolean
): Promise<{ success: boolean; id: string; is_active: boolean }> => {
  const { data, error } = await (supabase.rpc as any)('admin_toggle_survey_question', {
    p_admin_session_token: adminSessionToken,
    p_question_id: questionId,
    p_is_active: isActive,
  });
  if (error) throw error;
  return data;
};

export const adminDeleteSurveyQuestion = async (
  adminSessionToken: string,
  questionId: string
): Promise<{ success: boolean }> => {
  const { data, error } = await (supabase.rpc as any)('admin_delete_survey_question', {
    p_admin_session_token: adminSessionToken,
    p_question_id: questionId,
  });
  if (error) throw error;
  return data;
};

export const adminReorderSurveyQuestions = async (
  adminSessionToken: string,
  questionIds: string[]
): Promise<{ success: boolean; count: number }> => {
  const { data, error } = await (supabase.rpc as any)('admin_reorder_survey_questions', {
    p_admin_session_token: adminSessionToken,
    p_question_ids: questionIds,
  });
  if (error) throw error;
  return data;
};

export const adminSetSurveyPhaseState = async (
  adminSessionToken: string,
  phase: SurveyPhase,
  state: SurveyPhaseManualState,
  // v6.11.50: obligatorio en la práctica. Antes el RPC hacía UPDATE sobre la
  // VIEW app_settings y terminaba abriendo/cerrando la encuesta de la liga
  // enabled más vieja, sin importar sobre cuál estuviera operando el admin.
  leagueId?: string | null,
): Promise<{ success: boolean; phase: string; state: string }> => {
  const { data, error } = await (supabase.rpc as any)('admin_set_survey_phase_state', {
    p_admin_session_token: adminSessionToken,
    p_phase: phase,
    p_state: state,
    p_league_id: leagueId ?? null,
  });
  if (error) throw error;
  return data;
};

export interface SurveyResults {
  phase: SurveyPhase;
  manual_state: SurveyPhaseManualState;
  is_open: boolean;
  total_responses: number;
  anonymous_count: number;
  by_branch: Record<string, number>;
  by_category: Record<string, number>;
  questions: Array<{
    id: string;
    phase: SurveyPhase;
    category: string;
    question_text: string;
    question_type: SurveyQuestionType;
    options: string[] | null;
    is_active: boolean;
    response_count: number;
    rating_stats: { avg: number; distribution: Record<string, number> } | null;
    radio_stats: Record<string, number> | null;
    checkbox_stats: Record<string, number> | null;
    text_responses: Array<{
      response_id: string;
      submitted_at: string;
      is_anonymous: boolean;
      branch: string | null;
      category_id: string | null;
      category_name: string | null;
      user_id: string | null;
      user_name: string | null;
      team_name: string | null;
      text: string;
    }> | null;
  }>;
}

export const adminGetSurveyResults = async (
  adminSessionToken: string,
  phase: SurveyPhase,
  branch: string | null = null,
  categoryId: string | null = null,
  leagueId?: string | null
): Promise<SurveyResults> => {
  const { data, error } = await (supabase.rpc as any)('admin_get_survey_results', {
    p_admin_session_token: adminSessionToken,
    p_phase: phase,
    p_branch: branch,
    p_category_id: categoryId,
    p_league_id: leagueId ?? null,
  });
  if (error) throw error;
  return data;
};
